import { DSTooltip } from '@repo/design-system/index'
import Image from 'next/image'
import { useMemo, useState } from 'react'
import { AVERAGE_TYPES, CAFAverageTypes, METRIC_TAG } from '../../../../../model/caf.interface'
import { useCafAnalysisAverageScores, useCafAnalysisComponentDetailList } from '../../../../../modules/apis/webview/caf'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { s_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { ICON_QUESTION_GRAY700 } from '../../../../../modules/svg/svg'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import BarChart from '../../charts/BarChart'
import BarRatioChart from '../../charts/BarRatioChart'
import SpeechRateChart from '../../charts/SpeechRateChart'
import HighlightedText from '../../common/HighlightedText'
import { useWebviewModal } from '../../common/Modal/WebviewModal'
import WideRectangleTabs from '../../common/WideRectangleTabs'

export const tabListMap = {
  [METRIC_TAG.speech_rate]: {
    title: s_caf.fluency.tab1,
    subtitle: (value) => Number(value)?.toFixed(0) + ' wpm',
    value: METRIC_TAG.speech_rate,
  },
  [METRIC_TAG.mlr]: {
    title: s_caf.fluency.tab2,
    subtitle: (value) => Number.parseFloat(value).toFixed(2) + ' words',
    value: METRIC_TAG.mlr,
  },
  [METRIC_TAG.filler_word]: {
    title: s_caf.fluency.tab3,
    subtitle: (value) => Number.parseFloat(value).toFixed(2) + '%',
    value: METRIC_TAG.filler_word,
  },
  [METRIC_TAG.pause]: {
    title: s_caf.fluency.tab4,
    subtitle: (value) => Number.parseFloat(value).toFixed(2) + '%',
    value: METRIC_TAG.pause,
  },
  [METRIC_TAG.ngram]: {
    title: s_caf.fluency.tab5,
    subtitle: (value) => Number.parseFloat(value).toFixed(2) + '%',
    value: METRIC_TAG.ngram,
  },
  [404]: {
    title: '',
    subtitle: () => '',
    value: '',
  },
}

const FluencyTabsSection = ({ componentId }) => {
  const { t } = useTranslation()
  const { data, isLoading } = useCafAnalysisComponentDetailList(componentId)
  const [activeIndex, setActiveIndex] = useState(METRIC_TAG.speech_rate)

  const graphData = useMemo(
    () => data?.metrics.find((metric) => metric.metric_tag === activeIndex),
    [data, activeIndex]
  )

  const type = METRIC_TAG[activeIndex] as CAFAverageTypes
  const withUnit = tabListMap[activeIndex]?.subtitle
  if (isLoading || !data)
    return (
      <div className="flex h-[100px] w-screen items-center justify-center">
        <LoadingSpinner show primary />
      </div>
    )
  return (
    <>
      <div className="text-h5 mb-[12px] ml-[20px] text-gray-700">{t(s_caf.fluency.metric)}</div>
      <WideRectangleTabs
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        tabList={data.metrics.map((mtrItem) => {
          return { ...tabListMap[mtrItem.metric_tag], tab: mtrItem.value }
        })}
      />
      <div className="mx-[20px]">
        <FluencyChart activeIndex={activeIndex} graphData={graphData?.graph_data.data} />
        <Average thisClassAverage={graphData?.value || 0} withUnit={withUnit} type={type} />
      </div>
    </>
  )
}

interface AverageProps {
  thisClassAverage: number | string
  type: CAFAverageTypes
  withUnit: (value: string | number) => string
}
export const Average = ({ thisClassAverage, type, withUnit }: AverageProps) => {
  const { data, isLoading } = useCafAnalysisAverageScores(type)
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  if (Object.keys(AVERAGE_TYPES).findIndex((key) => key === type) < 0 || isLoading) {
    return <></>
  }
  const average1 = withUnit(thisClassAverage)
  const average2 = withUnit(data?.top_25_ratio?.value || 0)
  const average3 = withUnit(data?.native_ratio?.value || 0)

  if (isMobile) {
    return (
      <div className="mt-[12px] flex min-h-[68px] rounded-[6px] border border-gray-200 bg-white py-[8px] px-[12px]">
        <div className="border-r-1 flex flex-auto flex-col items-center justify-between border-gray-300 px-[8px] text-left">
          <div className="text-sBody w-[100%] text-gray-700 ">{t(s_caf.fluency.myav)}</div>
          <div className="w-[100%]">
            <HighlightedText color={'rgba(122,93,232,0.2)'} text={average1} />
          </div>
        </div>
        <div className="border-r-1 flex flex-auto flex-col items-center justify-between border-gray-300 px-[8px] text-left">
          <div className="text-sBody w-[100%] text-gray-700">{t(s_caf.fluency.top)}</div>
          <div className="w-[100%]">
            <HighlightedText color={'rgba(255,238,197,1)'} text={average2} />
          </div>
        </div>
        <div className="flex flex-auto flex-col items-center justify-between px-[8px] text-left">
          <div className="text-sBody w-[100%] text-gray-700 ">{t(s_caf.fluency.tutor)}</div>
          <div className="w-[100%]">
            <HighlightedText color={'rgba(234, 245, 255, 1)'} text={average3} />
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="mt-[12px] flex min-h-[84px] rounded-[6px] border border-gray-200 bg-white py-[12px] px-[16px]">
        <div className="border-r-1 flex flex-auto flex-col items-center justify-between border-gray-300 px-[8px] text-left">
          <div className="w-[100%] text-[16px] text-gray-700 ">{t(s_caf.fluency.myav)}</div>
          <div className="w-[100%]">
            <HighlightedText color={'rgba(122,93,232,0.2)'} text={average1} />
          </div>
        </div>
        <div className="border-r-1 flex flex-auto flex-col items-center justify-between border-gray-300 px-[8px] text-left">
          <div className="w-[100%] text-[16px] text-gray-700">{t(s_caf.fluency.top)}</div>
          <div className="w-[100%]">
            <HighlightedText color={'rgba(255,238,197,1)'} text={average2} />
          </div>
        </div>
        <div className="flex flex-auto flex-col items-center justify-between px-[8px] text-left">
          <div className="w-[100%] text-[16px] text-gray-700 ">{t(s_caf.fluency.tutor)}</div>
          <div className="w-[100%]">
            <HighlightedText color={'rgba(234, 245, 255, 1)'} text={average3} />
          </div>
        </div>
      </div>
    )
  }
}
const POPUP_TEXT_MAP = {
  [METRIC_TAG.speech_rate]: {
    title: s_caf.fluency.modal_title1,
    body: s_caf.fluency.modal_subtitle1,
  },
  [METRIC_TAG.mlr]: {
    title: s_caf.mlr_def.modal_title,
    body: s_caf.mlr_def.modal_body,
  },
  [METRIC_TAG.filler_word]: {
    title: s_caf.fluency.modal_title3,
    body: s_caf.fluency.modal_subtitle3,
  },
  [METRIC_TAG.pause]: {
    title: s_caf.fluency.modal_title4,
    body: s_caf.fluency.modal_subtitle4,
  },
  [METRIC_TAG.ngram]: {
    title: s_caf.fluency.modal_title5,
    body: s_caf.fluency.modal_subtitle5,
  },
}
export const FluencyChart = ({ activeIndex, graphData }) => {
  const webviewPopup = useWebviewModal()
  const { t } = useTranslation()
  const handleClickTooltip = () => {
    webviewPopup.show({
      id: 'speech_popup',
      title: t(POPUP_TEXT_MAP[activeIndex].title),
      body: t(POPUP_TEXT_MAP[activeIndex].body),
    })
  }

  const products = {
    [METRIC_TAG.speech_rate]: (
      <TabBody
        title={s_caf.fluency.graph_name1}
        handleClickTooltip={handleClickTooltip}
        activeIndex={activeIndex}
        graph={<SpeechRateChart data={graphData} dataKey={'wpm'} indexBy={'sentence'} pl={16} />}
      />
    ),
    [METRIC_TAG.mlr]: (
      <TabBody
        title={s_caf.fluency.graph_name2}
        handleClickTooltip={handleClickTooltip}
        activeIndex={activeIndex}
        graph={<BarChart data={graphData} dataKey={'mlr'} type="fluency" />}
      />
    ),
    [METRIC_TAG.filler_word]: (
      <TabBody
        title={s_caf.fluency.graph_name3}
        handleClickTooltip={handleClickTooltip}
        activeIndex={activeIndex}
        graph={
          <BarRatioChart
            data={graphData}
            dataKey={'filler_ratio'}
            indexBy={'turn'}
            tooltipName={t(s_caf.highlight.category16)}
          />
        }
      />
    ),
    [METRIC_TAG.pause]: (
      <TabBody
        title={s_caf.fluency.graph_name4}
        handleClickTooltip={handleClickTooltip}
        activeIndex={activeIndex}
        graph={
          <BarRatioChart
            data={graphData}
            dataKey={'pause_ratio'}
            indexBy="turn"
            tooltipName={t(s_caf.accuracy.error_type18)}
          />
        }
      />
    ),
    [METRIC_TAG.ngram]: (
      <TabBody
        title={s_caf.fluency.graph_name5}
        handleClickTooltip={handleClickTooltip}
        activeIndex={activeIndex}
        graph={
          <BarRatioChart
            data={graphData}
            dataKey={'ngram_ratio'}
            indexBy={'turn'}
            tooltipName={t(s_caf.accuracy.error_type14)}
          />
        }
      />
    ),
  }

  return products[activeIndex]
}

const TabBody = ({
  title = null,
  graph,
  handleClickTooltip,
  activeIndex,
}: {
  title: any
  graph?: any
  handleClickTooltip: () => void
  activeIndex: any
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <section className="mt-[24px] rounded-[6px] border border-gray-200 bg-white pt-[16px]">
        <div className="flex items-center">
          <h4 className="mb-[12px] h-[12px] pl-[16px] text-[14px] font-medium tracking-tight">{t(title)}</h4>
          <Image
            src={ICON_QUESTION_GRAY700}
            width={16}
            height={16}
            alt="question"
            className="ml-7 mb-[6px]"
            onClick={handleClickTooltip}
          />
        </div>
        <div className="mb-[12px]">{graph}</div>
      </section>
    )
  } else {
    return (
      <section className=" rounded-[6px] border border-gray-200 bg-white pt-[16px]">
        <div className="flex items-center">
          <div className="flex w-[100%] flex-row items-center justify-center">
            <h4 className="mb-[12px] h-[12px] pl-[16px] text-[14px] font-medium tracking-tight">{t(title)}</h4>
            <DSTooltip
              place="bottom"
              isMultiline={true}
              isOpenOnClick={true}
              triggerComponent={
                <Image src={ICON_QUESTION_GRAY700} width={16} height={16} alt="question" className="ml-7 mb-[6px]" />
              }
              contentComponent={t(POPUP_TEXT_MAP[activeIndex].body)}
            />
          </div>
        </div>
        <div className="mb-[12px]">{graph}</div>
      </section>
    )
  }
}

export default FluencyTabsSection
